/* eslint-disable react/jsx-key */
import { PropsWithChildren } from 'react';
import { twMerge } from 'tailwind-merge';
import { isNotNullNorUndefined } from '@silo/utils';
import {
  ExtendedRangePoint,
  POINTS_SMALL_ICON_WIDTH,
  PositionedPoints,
  RangePoint,
  Tooltip,
  TooltipContent,
  TooltipOrigin,
} from '@silo/ui';

type Props = {
  data: {
    current: number;
    low: number;
    optimal: number;
    critical: number;
    new?: number;
  };
  hasLow: boolean;
  hasOptimal: boolean;
};

const BLUE = '#49A4FF';
const GREEN = '#34D399';
const RED = '#FB7185';

export const UtilizationChart = (props: Props) => {
  const { data, hasLow, hasOptimal } = props;
  const hasOnlyCritical = !hasLow && !hasOptimal;

  const gradient = [
    ...(hasLow ? [{ color: BLUE, value: data.low }] : []),
    ...(hasOptimal ? [{ color: GREEN, value: data.optimal }] : []),
    ...(hasOnlyCritical ? [{ color: GREEN, value: data.critical }] : []),
    { color: RED, value: data.critical },
  ];

  const gradientStyle = `linear-gradient(90deg, ${gradient.map((stop) => `${stop.color} ${stop.value}%`)})`;

  const points = [...(hasLow ? [data.low] : []), ...(hasOptimal ? [data.optimal] : []), data.critical, data.new].filter(
    isNotNullNorUndefined
  );

  return (
    <div className="relative w-full mb-4 h-28">
      <div className="absolute top-0 left-0 z-0 grid w-full h-full grid-cols-5">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="relative border-l border-dashed last:border-r border-slate-700" />
        ))}
      </div>

      <div className={`${data.new ? 'top-2/3' : 'top-1/2'} relative -translate-y-1/2`}>
        <PositionedPoints
          points={points}
          indicators={[
            hasLow ? (
              <RangePoint
                label={
                  <Tooltip placement="bottom" gutter={20} key="low">
                    <TooltipOrigin>
                      <Label>Low</Label>
                    </TooltipOrigin>
                    <TooltipContent>
                      <span className="text-sm">
                        <span className="font-medium">Low utilization: </span>
                        {data.low}%
                      </span>
                    </TooltipContent>
                  </Tooltip>
                }
                direction="down"
                iconClassName="text-blue-400"
                size={POINTS_SMALL_ICON_WIDTH}
              />
            ) : undefined,
            hasOptimal ? (
              <RangePoint
                label={
                  <Tooltip placement="bottom" gutter={20} key="optimal">
                    <TooltipOrigin>
                      <Label>Optimal</Label>
                    </TooltipOrigin>
                    <TooltipContent>
                      <span className="text-sm">
                        <span className="font-medium">Optimal utilization: </span>
                        {data.optimal}%
                      </span>
                    </TooltipContent>
                  </Tooltip>
                }
                direction="down"
                iconClassName="text-emerald-400"
                size={POINTS_SMALL_ICON_WIDTH}
              />
            ) : undefined,
            <RangePoint
              label={
                <Tooltip placement="bottom" gutter={20} key="critical">
                  <TooltipOrigin>
                    <Label>Critical</Label>
                  </TooltipOrigin>
                  <TooltipContent>
                    <span className="text-sm">
                      <span className="font-medium">Critical utilization: </span>
                      {data.critical}%
                    </span>
                  </TooltipContent>
                </Tooltip>
              }
              direction="down"
              iconClassName="text-rose-400"
              size={POINTS_SMALL_ICON_WIDTH}
            />,
            data.new ? (
              <ExtendedRangePoint
                label={
                  <Label key="new" className="text-white">
                    New
                  </Label>
                }
                direction="down"
                iconClassName="text-violet-400"
                size={POINTS_SMALL_ICON_WIDTH}
              />
            ) : undefined,
          ].filter(Boolean)}
        />

        <div className="h-[2px] z-10 relative" style={{ background: gradientStyle }}></div>

        <PositionedPoints
          points={[data.current]}
          indicators={[
            <RangePoint
              label={
                <Label key="current" className="text-white">
                  Current
                </Label>
              }
              direction="up"
              iconClassName="text-white"
              size={POINTS_SMALL_ICON_WIDTH}
            />,
          ]}
        />
      </div>

      <div className="absolute bottom-0 grid w-full grid-cols-5 text-xs text-white" aria-hidden>
        {[...Array(5)].map((_, index, array) => (
          <div className="relative" key={index}>
            <span className="absolute bottom-0 -translate-x-1/2 top-1">{(index / array.length) * 100}%</span>
            {index === array.length - 1 ? <span className="absolute right-0 translate-x-1/2 top-1">100%</span> : null}
          </div>
        ))}
      </div>
    </div>
  );
};

const Label = ({ className, children }: PropsWithChildren<{ className?: string }>) => {
  return <span className={twMerge('font-mono text-xs text-shale-200', className)}>{children}</span>;
};
